/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Router } from '@angular/router'

import { CacheService } from '../global/ng2-cache/services/cache.service';
import { JwtHelper } from 'angular2-jwt';

import { SessionService } from './session2.service';
import { EventService } from './event.service';
import { CookieService } from 'ngx-cookie-service';
import { UserService } from './user.service';

@Injectable()
export class AuthService {
  private srToken: string = '';
  private _currentRole: any = { title: 'public', mask: 1 };
  private redirectUrl = null;
  private user = null;

  // dynamically generated from the server - hard-coded here
  private _userRoles = {
    public: { mask: 1, title: 'public' },
    contributor: { mask: 2, title: 'contributor' },
    user: { mask: 4, title: 'user' },
    manager: { mask: 8, title: 'manager' },
    admin: { mask: 16, title: 'admin' },
    sadmin: { mask: 32, title: 'sadmin' },
    uadmin: { mask: 64, title: 'uadmin' }
  };

  // dynamically generated from the server - hard-coded here
  private _accessLevels = {
    public: { title: 'public', mask: 127 },
    contributor: { title: 'contributor', mask: 126 },
    user: { title: 'user', mask: 124 },
    manager: { title: 'manager', mask: 120 },
    admin: { title: 'admin', mask: 112 },
    sadmin: { title: 'sadmin', mask: 96 },
    uadmin: { title: 'uadmin', mask: 64 }
  };

  constructor(private http: HttpClient,
    private cacheService: CacheService,
    private sessionService: SessionService,
    private eventService: EventService,
    private router: Router,
    private jwtHelper: JwtHelper,
    private cookieService: CookieService,
    private userService: UserService) {
    if (this.isLoggedIn) {
      this.srToken = localStorage.getItem('sr_token');
      this.resolveCurrentRole();
    }
  }

  login(email, password) {
    return this.http
      .post(
        '/api/v1/login',
        JSON.stringify({ email, password })
      ).map((response: any) => {
          this.srToken = response.sr_token;
          localStorage.setItem('sr_token', this.srToken);
          this.cacheService.set('session', response.session);
          this.resolveCurrentRole(); // need to set role to current role for session account
          this.sessionService.getSession(true); // force refresh of anything session-based (like the nav dropdown)
          this.eventService.loggedIn$.emit(true);
          this.redirectToUrl();
          return response;
        }
      );
  }

  loginGoogle(email, token) {
    return this.http
      .post(
        '/api/v1/logingoogle',
        JSON.stringify({ email, token })
      ).map((response: any) => {
        this.srToken = response.sr_token;
        localStorage.setItem('sr_token', this.srToken);
        this.cacheService.set('session', response.session);
        this.resolveCurrentRole(); // need to set role to current role for session account
        this.sessionService.getSession(true); // force refresh of anything session-based (like the nav dropdown)
        this.eventService.loggedIn$.emit(true);
        this.redirectToUrl();
        return response;
      });
  }

  logout(): Observable<any> {
    localStorage.removeItem('sr_token');
    document.cookie=`DrifttAuth=;expires=${new Date()};path=/`
    this.cacheService.removeAll();
    this.eventService.loggedIn$.emit(false);
    return Observable.of(true);
  }

  loginDrift() {
    return this.http
    .get(
      `/api/v1/logindrift`
    );
  }

  pwReset(emailAddress: string) {
    return this.http
      .post(
        `/api/v1/pwreset`,
        { email_address: emailAddress }
      );
  }

  getEmailAddressByToken(token: string) {
    return this.http
      .post(
        `/api/v1/pwtoken`,
        { token: token }
      );
  }

  changePassword(token: string, emailAddress: string, password: string) {
    return this.http
      .post(
        `/api/v1/pwchange`,
        { token: token, email_address: emailAddress, password: password }
      );
  }

  /**
   * Determines if the user has access to the route based on account-level permissions
   */
  async hasPermission(routeRole: any) {
    let neededRole = this._accessLevels[routeRole];
    if (!neededRole) {
      return false;
    }

    let userRole = this.userRoles.public;
    if (this.isLoggedIn) {
      if (!this.user) {
        this.user = await this.userService.getProfile().toPromise()
      }
      const permissions = this.user.permissions
      if (permissions.system_role.mask > 16) {
        // if system role
        userRole = permissions.system_role;
        this._currentRole = permissions.system_role;
      }
      else {
        let accountId = this.cacheService.get('session') ? this.cacheService.get('session').account_id ? this.cacheService.get('session').account_id : '' : '';
        for (let ap of permissions.account_roles) {
          if (ap.account_id == accountId) {
            userRole = ap.role;
            this._currentRole = ap.role;
            break;
          }
        }
      }
    }

    if (!(neededRole.mask & userRole.mask)) {
      return false;
    }
    return true;
  }

  private async resolveCurrentRole() {
    if (!this.user) {
      this.user = await this.userService.getProfile().toPromise()
    }
    const permissions = this.user.permissions
    if (permissions.system_role.mask > 16) {
      // if system role
      this._currentRole = permissions.system_role;
    }
    else {
      let accountId = this.cacheService.get('session') ? this.cacheService.get('session').account_id ? this.cacheService.get('session').account_id : '' : '';
      for (let ap of permissions.account_roles) {
        if (ap.account_id == accountId) {
          this._currentRole = ap.role;
          break;
        }
      }
    }
  }

  private redirectToUrl() {
    if (this.redirectUrl) {
      this.router.navigateByUrl(this.redirectUrl);
      this.redirectUrl = null
    }
    else{
      this.router.navigate(['']);
    }
  }

  public setRedirectUrl(value) {
    this.redirectUrl = value;
  }

  get isLoggedIn(): boolean {
    return !!localStorage.getItem('sr_token') || !!this.cookieService.get('DrifttAuth');
  }

  get token(): string {
    return this.srToken;
  }

  async getCurrentUserId() {
    if (!this.user) {
      this.user = await this.userService.getProfile().toPromise()
    }
    return this.user._id
  }

  get currentRole(): any {
    return this._currentRole;
  }

  get isContributor(): boolean {
    return this._currentRole.title == 'contributor';
  }

  async getHasLabsAccess() {
    if (!this.user) {
      this.user = await this.userService.getProfile().toPromise()
    }
    return this.user.permissions.labs_access
  }

  async getAccountCount() {
    if (!this.user) {
      this.user = await this.userService.getProfile().toPromise()
    }
    return this.user.permissions.account_roles.length
  }

  get userRoles(): any {
    return this._userRoles;
  }

  get accessLevels(): any {
    return this._accessLevels;
  }
}



// WEBPACK FOOTER //
// ./client/app/services/auth.service.ts